import { zonedDateTimeToDate } from "@redotech/util/temporal";

export const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

export const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "numeric",
});

const SHORT_DAY_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
});

export function formatDate(
  temporal:
    | Temporal.PlainDate
    | Temporal.PlainDateTime
    | Temporal.Instant
    | Temporal.ZonedDateTime,
) {
  if (temporal instanceof Temporal.ZonedDateTime) {
    return DATE_FORMAT.format(
      new Date(temporal.year, temporal.month - 1, temporal.day),
    );
  }
  return DATE_FORMAT.format(new Date(String(temporal)));
}

export function formatDateTime(
  temporal: Temporal.PlainDate | Temporal.PlainDateTime | Temporal.Instant,
) {
  return DATE_TIME_FORMAT.format(new Date(String(temporal)));
}

export function formatShortDayDate(
  temporal: Temporal.PlainDate | Temporal.ZonedDateTime,
) {
  return SHORT_DAY_DATE_FORMAT.format(
    temporal instanceof Temporal.ZonedDateTime
      ? zonedDateTimeToDate(temporal)
      : new Date(temporal.year, temporal.month - 1, temporal.day),
  );
}

/**
 * Convert a calendar date to the absolute Date that represents 00:00:00 local
 * time for that day in the given IANA timezone. Use this on analytics pages
 * for the start of a date-range filter so the merchant's "Mar 12" matches
 * everything that happened on Mar 12 in their local time.
 */
export function plainDateToStartOfDay(
  pd: Temporal.PlainDate,
  timezone: string,
): Date {
  return new Date(
    pd
      .toZonedDateTime({
        timeZone: timezone,
        plainTime: Temporal.PlainTime.from({ hour: 0, minute: 0, second: 0 }),
      })
      .toInstant().epochMilliseconds,
  );
}

/**
 * Convert a calendar date to the absolute Date that represents 23:59:59.999
 * local time for that day in the given IANA timezone. Use this on analytics
 * pages for the end of a date-range filter so picking "Mar 29" actually
 * includes everything that happened on Mar 29 (instead of cutting off at
 * midnight UTC).
 */
export function plainDateToEndOfDay(
  pd: Temporal.PlainDate,
  timezone: string,
): Date {
  return new Date(
    pd
      .toZonedDateTime({
        timeZone: timezone,
        plainTime: Temporal.PlainTime.from({
          hour: 23,
          minute: 59,
          second: 59,
          millisecond: 999,
        }),
      })
      .toInstant().epochMilliseconds,
  );
}
